import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Wallet, User as UserIcon, TrendingUp, Info } from "lucide-react";

/**
 * Contractor Cost tab — internal margin tracking.
 * Mirrors the Project Hub layout (milestone → tasks) but:
 *   - Shows task DESCRIPTION only (no homeowner-facing price).
 *   - Adds an inline-editable `contractor_cost` per task (click, type, blur to save).
 *   - Milestone header shows: progress % + subtotal contractor cost for that milestone.
 *   - Page header shows: charged / to-contractors / margin summary.
 *
 * Access: rendered only for admin/pm/designer. The backend also strips the
 * `contractor_cost` field for customer/contractor roles as belt-and-braces.
 */
export default function ContractorCostTab({ projectId, project, milestones = [], tasks = [], firmContractors = [], readOnly = false, onChange }) {
  const [collapsed, setCollapsed] = useState(() => new Set());

  const contractorNameById = useMemo(() => {
    const m = new Map();
    for (const c of firmContractors) m.set(c.contractor_id, c.name);
    return m;
  }, [firmContractors]);

  const grouped = useMemo(() => {
    const byMs = new Map();
    const orphaned = [];
    for (const ms of milestones) byMs.set(ms.milestone_id, { ms, tasks: [] });
    for (const t of tasks) {
      if (t.milestone_id && byMs.has(t.milestone_id)) byMs.get(t.milestone_id).tasks.push(t);
      else orphaned.push(t);
    }
    const rows = milestones.map((m) => byMs.get(m.milestone_id)).filter(Boolean);
    if (orphaned.length) rows.push({ ms: { milestone_id: null, name: "Uncategorised", order: 999 }, tasks: orphaned });
    return rows;
  }, [milestones, tasks]);

  // Project-level rollups.
  // "Charged to homeowner" = the authoritative contract value shown to the
  // customer (Original contract + Approved VOs). We deliberately do NOT sum
  // task `line_total` here — that misses unpriced tasks and VO-created tasks
  // and drifts from the contract on Overview / Payments.
  const totals = useMemo(() => {
    const charged = Number(project?.current_project_value || 0);
    let cost = 0;
    let assigned = 0;
    for (const t of tasks) {
      const c = Number(t.contractor_cost || 0);
      cost += c;
      if (t.contractor_cost != null) assigned += 1;
    }
    const margin = charged - cost;
    const marginPct = charged > 0 ? (margin / charged) * 100 : 0;
    return { charged, cost, margin, marginPct, assigned, totalTasks: tasks.length };
  }, [tasks, project]);

  const toggle = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="space-y-6">
      {/* Page header — margin summary */}
      <div className="card-soft" data-testid="cc-summary">
        <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] flex items-center gap-2">
          Project margin
          <span title="Charged to homeowner (Original contract + Approved VOs) minus the total contractor cost. Internal only." className="text-[#7A7571]">
            <Info size={12} />
          </span>
        </div>
        <div className="grid sm:grid-cols-4 gap-4 sm:gap-6 mt-3">
          <Money label="Charged to homeowner" value={totals.charged} testid="cc-total-charged" />
          <Money label="To contractors" value={totals.cost} testid="cc-total-cost" />
          <Money label="Margin" value={totals.margin} tone={totals.margin >= 0 ? "positive" : "danger"} testid="cc-total-margin" bold />
          <div>
            <div className="text-xs text-[#7A7571]">Margin %</div>
            <div
              className={`font-display text-lg font-bold ${totals.margin >= 0 ? "text-[#166534]" : "text-[#9F1239]"}`}
              data-testid="cc-total-margin-pct"
            >
              {totals.charged > 0 ? `${totals.marginPct.toFixed(1)}%` : "—"}
            </div>
          </div>
        </div>
        <div className="text-[11px] text-[#7A7571] mt-2">
          {totals.assigned} of {totals.totalTasks} task{totals.totalTasks === 1 ? "" : "s"} have a contractor cost assigned.
        </div>
      </div>

      {grouped.length === 0 && (
        <div className="card-soft text-center py-16 text-[#7A7571]" data-testid="cc-empty">
          No milestones or tasks yet. Add tasks from the Project Hub, then come back to set contractor costs.
        </div>
      )}

      {grouped.map(({ ms, tasks: msTasks }) => {
        const isCollapsed = collapsed.has(ms.milestone_id || "orphan");
        const done = msTasks.filter((t) => t.status === "done" || t.status === "waiting_on_customer").length;
        const total = msTasks.length;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        const msCost = msTasks.reduce((s, t) => s + Number(t.contractor_cost || 0), 0);
        const msCharged = msTasks.reduce((s, t) => s + Number(t.line_total || 0), 0);
        const msMargin = msCharged - msCost;

        return (
          <div key={ms.milestone_id || "orphan"} className="card-soft !p-0 overflow-hidden" data-testid={`cc-ms-${ms.milestone_id || "orphan"}`}>
            {/* Milestone header row */}
            <button
              type="button"
              onClick={() => toggle(ms.milestone_id || "orphan")}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[#F7F4EC]/60 transition-colors text-left"
            >
              {isCollapsed ? <ChevronRight size={16} className="text-[#7A7571] flex-shrink-0" /> : <ChevronDown size={16} className="text-[#7A7571] flex-shrink-0" />}
              <span className="font-display text-lg font-bold text-[#2C2A29] flex-1 min-w-0 truncate">{ms.name}</span>
              {/* Milestone contractor-cost subtotal */}
              <span
                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#F7F4EC] text-[#2C2A29] border border-[#EBE5D9]"
                data-testid={`cc-ms-cost-${ms.milestone_id || "orphan"}`}
                title="Total contractor cost for this milestone"
              >
                <Wallet size={11} /> ${msCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {/* Milestone margin pill (replaces the old progress pill) */}
              {msCharged > 0 && (
                <span
                  className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${msMargin >= 0 ? "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" : "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]"}`}
                  data-testid={`cc-ms-margin-${ms.milestone_id || "orphan"}`}
                  title="Charged − contractor cost for this milestone"
                >
                  <TrendingUp size={11} /> {msMargin >= 0 ? "+" : "−"}${Math.abs(msMargin).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {" "}({msCharged > 0 ? ((msMargin / msCharged) * 100).toFixed(1) : "0.0"}%)
                </span>
              )}
            </button>

            {/* Task rows */}
            {!isCollapsed && (
              <div className="divide-y divide-[#EBE5D9]">
                {msTasks.length === 0 && (
                  <div className="px-5 py-4 text-sm text-[#7A7571] italic">No tasks under this milestone yet.</div>
                )}
                {msTasks.map((t) => (
                  <TaskCostRow
                    key={t.task_id}
                    task={t}
                    contractorName={contractorNameById.get(t.owner_contractor_id) || null}
                    readOnly={readOnly}
                    onSaved={onChange}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TaskCostRow({ task, contractorName, readOnly, onSaved }) {
  const initial = task.contractor_cost != null ? String(task.contractor_cost) : "";
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  // Set to true when Escape is pressed so the onBlur→commit path skips saving.
  const skipCommitRef = useRef(false);

  useEffect(() => {
    // Keep the local input in sync when parent reloads tasks (e.g. after PATCH).
    setValue(task.contractor_cost != null ? String(task.contractor_cost) : "");
  }, [task.contractor_cost]);

  const charged = Number(task.line_total || 0);
  const cost = Number(value || 0);
  const margin = charged - cost;
  const hasCharged = charged > 0;

  const commit = async () => {
    setEditing(false);
    if (skipCommitRef.current) {
      // Escape was pressed — abandon the pending save entirely.
      skipCommitRef.current = false;
      return;
    }
    const cleaned = value.trim();
    const parsed = cleaned === "" ? null : parseFloat(cleaned);
    const currentServer = task.contractor_cost == null ? null : Number(task.contractor_cost);
    const noChange = (parsed == null && currentServer == null) || (parsed != null && currentServer != null && parsed === currentServer);
    if (noChange) return;
    if (parsed != null && (isNaN(parsed) || parsed < 0)) {
      toast.error("Enter a valid positive number");
      setValue(currentServer != null ? String(currentServer) : "");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.task_id}/contractor-cost`, { contractor_cost: parsed });
      onSaved && (await onSaved());
    } catch (err) {
      toast.error(formatApiError(err));
      setValue(currentServer != null ? String(currentServer) : "");
    } finally { setSaving(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter") { e.currentTarget.blur(); }
    if (e.key === "Escape") {
      // Signal the imminent onBlur to skip saving, then reset the visible
      // value and let the input lose focus.
      skipCommitRef.current = true;
      setValue(task.contractor_cost != null ? String(task.contractor_cost) : "");
      setEditing(false);
      e.currentTarget.blur();
    }
  };

  return (
    <div className="grid grid-cols-12 items-center gap-x-3 gap-y-2 px-3 sm:px-5 py-3" data-testid={`cc-task-${task.task_id}`}>
      {/* Description */}
      <div className="col-span-12 md:col-span-5 min-w-0">
        <div className="text-sm text-[#2C2A29] font-medium truncate">{task.title || "Untitled task"}</div>
        {task.description && (
          <div className="text-xs text-[#7A7571] mt-0.5 line-clamp-2">{task.description}</div>
        )}
      </div>

      {/* Assigned contractor */}
      <div className="col-span-12 md:col-span-2 min-w-0" data-testid={`cc-task-contractor-${task.task_id}`}>
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#F5F3FF] text-[#5B21B6] border border-[#DDD6FE] max-w-full">
          <UserIcon size={11} className="flex-shrink-0" />
          <span className="truncate">{contractorName || "Unassigned"}</span>
        </div>
      </div>

      {/* Charged to client (line item price on quotation) — italic per user's spec */}
      <div className="col-span-6 md:col-span-2 text-left md:text-right min-w-0" data-testid={`cc-task-charged-${task.task_id}`}>
        {hasCharged ? (
          <>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#7A7571]">Charged</div>
            <div className="italic text-sm font-semibold text-[#2C2A29] break-words">
              ${charged.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </>
        ) : (
          <div className="italic text-xs text-[#7A7571] leading-tight">— no client price —</div>
        )}
      </div>

      {/* Cost editor + margin */}
      <div className="col-span-6 md:col-span-3 flex flex-col items-end min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-sm text-[#7A7571] flex-shrink-0">$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            disabled={readOnly || saving}
            data-testid={`cc-cost-input-${task.task_id}`}
            value={value}
            onFocus={() => setEditing(true)}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={onKey}
            placeholder="0.00"
            className={`w-28 text-right text-sm font-semibold rounded-lg border transition-colors ${saving ? "opacity-60" : ""} ${editing ? "border-[#E07A5F] bg-white" : "border-transparent bg-transparent hover:bg-[#F7F4EC] hover:border-[#EBE5D9]"} px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/40`}
          />
        </div>
        {hasCharged && task.contractor_cost != null && (
          <div
            className={`text-[11px] font-semibold mt-0.5 ${margin >= 0 ? "text-[#166534]" : "text-[#9F1239]"}`}
            data-testid={`cc-margin-${task.task_id}`}
            title={`Homeowner paid $${charged.toFixed(2)} · Contractor cost $${cost.toFixed(2)}`}
          >
            {margin >= 0 ? "+" : "−"}${Math.abs(margin).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {" "}({charged > 0 ? ((margin / charged) * 100).toFixed(1) : "0.0"}%)
          </div>
        )}
      </div>
    </div>
  );
}

function Money({ label, value, tone, bold, testid }) {
  const cls =
    tone === "positive" ? "text-[#166534]"
      : tone === "danger" ? "text-[#9F1239]"
        : "text-[#2C2A29]";
  return (
    <div>
      <div className="text-xs text-[#7A7571]">{label}</div>
      <div className={`font-display ${bold ? "text-2xl" : "text-lg"} font-bold ${cls}`} data-testid={testid}>
        ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}
